import { Globe, TrendingUp, Zap, BarChart3, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { useState } from "react";
import { PillarDetailDialog } from "@/components/PillarDetailDialog";
import { Button } from "@/components/ui/button";
import { CalendarDialog } from "@/components/CalendarDialog";

const pillars = [
  {
    icon: Globe,
    number: "01",
    title: "Foundation",
    subtitle: "Get Found. Build Trust. Look Like the Top Choice.",
    description: "Before anyone calls, they judge you online — instantly. This stage builds the digital foundation that earns trust before you ever speak to a customer.",
    tagline: "Dominate Google in your local suburbs.",
    cardSubtext: "Professional website, Google Business Profile optimisation & automated 5-star review system.",
    features: [
      "High-performance, mobile-first website design",
      "Fully optimised Google Business Profile",
      "Automated 5-star Google review collection",
      "Suburb-by-suburb local SEO ranking",
      "Professional branding that builds instant trust",
    ],
    outcomes: [
      "More qualified leads from your target suburbs",
      "Trust at first glance — customers choose you first",
      "Higher-value jobs without increasing ad spend",
      "Digital presence that matches your quality workmanship",
      "Stronger positioning against competitors",
    ],
  },
  {
    icon: TrendingUp,
    number: "02",
    title: "Growth",
    subtitle: "Predictable leads every single week.",
    description: "Stop guessing where your next job is coming from. This stage builds a reliable pipeline that delivers quality leads consistently.",
    tagline: "Turn on steady, reliable job flow.",
    cardSubtext: "Google Ads, Facebook Ads & targeted campaigns for trade businesses.",
    features: [
      "Targeted Google Ads for trade businesses",
      "Facebook Ads for construction companies",
      "High-converting landing pages",
      "Suburb + job-type precision targeting",
      "Lead filtering to eliminate tyre-kickers",
    ],
    outcomes: [
      "Predictable weekly enquiries",
      "Less downtime between jobs",
      "Better-fit clients in better suburbs",
      "More control over your workload",
      "Stronger, more profitable pipeline",
    ],
  },
  {
    icon: Zap,
    number: "03",
    title: "Automation",
    subtitle: "Win more work by replying first — every time.",
    description: "Trade businesses don't lose jobs because of poor work. They lose jobs because customers spoke to someone faster. Automation fixes that permanently.",
    tagline: "Never lose a job to slow follow-up again.",
    cardSubtext: "Instant SMS replies, missed-call text-back & smart lead qualification.",
    features: [
      "Instant SMS & AI-powered responses",
      "Missed-call text-back system",
      "Smart pre-qualification sequences",
      "Automated nurture & follow-up",
      "Lead tracking + appointment reminders",
    ],
    outcomes: [
      "More booked quotes without extra admin",
      "Zero missed opportunities",
      "Faster replies = higher win rates",
      "Less manual follow-up work",
      "Better customer experience",
    ],
  },
  {
    icon: BarChart3,
    number: "04",
    title: "Scale",
    subtitle: "Clarity, control and smarter growth.",
    description: "Once your system is live, we continually refine it — so your results improve every month and you can scale without stress.",
    tagline: "Know what's working. Grow strategically.",
    cardSubtext: "ROI dashboards, performance tracking & expansion strategy.",
    features: [
      "Clear reporting & ROI dashboards",
      "Suburb ranking + performance tracking",
      "Google review growth strategy",
      "Campaign optimisation",
      "Scaling plan for new suburbs & bigger projects",
    ],
    outcomes: [
      "Total clarity on marketing ROI",
      "Stronger competitive position",
      "Access to higher-value projects",
      "Profitable expansion into new areas",
      "Long-term, sustainable growth",
    ],
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.12,
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
    },
  }),
};

const numberVariants = {
  hidden: { opacity: 0, scale: 0.5 },
  visible: (i: number) => ({
    opacity: 1,
    scale: 1,
    transition: {
      delay: i * 0.12 + 0.2,
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
    },
  }),
};

export function SystemSection() {
  const [selectedPillar, setSelectedPillar] = useState<typeof pillars[0] | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleOpenDetails = (pillar: typeof pillars[0]) => {
    setSelectedPillar(pillar);
    setDialogOpen(true);
  };

  return (
    <section id="system-section" className="section-padding bg-muted relative overflow-hidden" aria-labelledby="system-heading">
      {/* Background elements */}
      <div className="absolute inset-0 grid-pattern opacity-40" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[300px] sm:w-[500px] h-[150px] sm:h-[250px] bg-primary/[0.05] rounded-full blur-[80px] sm:blur-[100px]" />
      
      <div className="container-wide relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mx-auto text-center mb-6 sm:mb-8"
        >
          <span className="section-badge mb-3 sm:mb-4">
            <span>The FullScale Growth System™</span>
          </span>
          <h2 id="system-heading" className="heading-section text-foreground mb-3 text-balance">
            The Complete Digital Marketing System for{" "}
            <span className="text-gradient">Australian Trade Businesses</span>
          </h2>
          <p className="text-[0.9375rem] sm:text-base text-muted-foreground max-w-xl mx-auto leading-[1.7]">
            Here's how it works — a modular growth system designed exclusively for trade, construction & home service businesses across Sydney, Melbourne, Brisbane, Perth and Australia-wide.
          </p>
        </motion.div>

        {/* Pillars Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {pillars.map((pillar, index) => (
            <motion.article
              key={pillar.number}
              custom={index}
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              whileHover={{ y: -4 }}
              transition={{ duration: 0.3 }}
              className="group relative bg-card rounded-xl p-4 sm:p-5 shadow-card border border-border/60 hover:shadow-xl hover:border-primary/30 transition-all duration-400 cursor-pointer flex flex-col"
              onClick={() => handleOpenDetails(pillar)}
            >
              {/* Animated number watermark */}
              <motion.span 
                custom={index}
                variants={numberVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="absolute top-3 right-3 text-[36px] sm:text-[42px] font-bold text-primary/[0.08] leading-none select-none group-hover:text-primary/[0.15] transition-colors duration-300"
                aria-hidden="true"
              >
                {pillar.number}
              </motion.span>

              {/* Icon */}
              <div className="relative z-10 w-9 sm:w-10 h-9 sm:h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3 group-hover:bg-primary/20 transition-all duration-300" aria-hidden="true">
                <pillar.icon className="w-4 sm:w-5 h-4 sm:h-5 text-primary" />
              </div>

              {/* Content */}
              <div className="relative z-10 flex-1 flex flex-col">
                <span className="text-[0.6875rem] sm:text-xs font-bold text-primary uppercase tracking-wider mb-0.5">
                  Pillar {pillar.number}
                </span>
                <h3 className="text-base sm:text-lg font-bold text-foreground mb-1.5">
                  {pillar.title}
                </h3>
                <p className="text-foreground text-[0.9375rem] sm:text-base font-medium leading-[1.5] mb-1">
                  {pillar.tagline}
                </p>
                <p className="text-muted-foreground text-[0.9375rem] sm:text-base leading-[1.65] mb-3 flex-1">
                  {pillar.cardSubtext}
                </p>

                {/* CTA Button */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-between text-primary hover:text-primary hover:bg-primary/10 group/btn h-8 text-[0.8125rem] sm:text-[0.875rem]"
                >
                  <span className="font-semibold">See What's Included</span>
                  <ArrowRight className="w-3 h-3 group-hover/btn:translate-x-1 transition-transform" />
                </Button>
              </div>

              {/* Hover glow effect */}
              <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400 pointer-events-none" />
            </motion.article>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="mt-6 sm:mt-8 text-center"
        >
          <p className="text-muted-foreground mb-1.5 text-[0.875rem] sm:text-[0.9375rem]">
            Not sure which stage your business needs first?
          </p>
          <CalendarDialog
            trigger={
              <button className="inline-flex items-center gap-1.5 text-primary font-semibold hover:underline underline-offset-4 text-[0.875rem] sm:text-[0.9375rem]">
                Book a free strategy call — we'll map it out together
                <ArrowRight className="w-3 h-3" />
              </button>
            }
          />
        </motion.div>
      </div>

      {/* Detail Dialog */}
      <PillarDetailDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        pillar={selectedPillar}
      />
    </section>
  );
}
